/**
 * Comment text for notifications (GH #2795, spec docs/ux/2026-10-25-comment-notifications.md §3).
 *
 * One flattener for every channel: the server flattens once and the toast and the service worker show the result,
 * each cut to its own length with `truncateAtWord`.
 */

export interface PlainTextOptions {
  /**
   * The chord's name as charted, not transposed. Injected because the namer lives in `@tunasong/music-lib`,
   * which depends on this package.
   */
  chordName: (chord: unknown) => string
}

/** Void elements named in brackets (N8–N10); any other element contributes its children's text */
const imageTypes = new Set(['image'])
const audioTypes = new Set(['audio', 'audioclip', 'audiotrack', 'audiocomment'])
const attachmentTypes = new Set(['video', 'pdf', 'midi', 'musicxml'])

/** Elements that sit inside a line; everything else is a block, separated from its neighbours by a space */
const inlineTypes = new Set(['mention', 'chord', 'link', 'entitylink'])

interface Part {
  text: string
  /** Words a reader wrote (text, mentions, chords, links), as opposed to an attachment's placeholder */
  isText: boolean
}

interface SlateNode {
  text?: unknown
  type?: unknown
  children?: unknown
  name?: unknown
  email?: unknown
  ref?: { name?: unknown }
}

const asNode = (node: unknown): SlateNode => (typeof node === 'object' && node !== null ? (node as SlateNode) : {})

const childrenOf = (node: SlateNode): unknown[] => (Array.isArray(node.children) ? node.children : [])

const mentionName = (node: SlateNode) => {
  if (typeof node.name === 'string' && node.name.trim()) {
    return node.name.trim()
  }
  /** Never the full address (§2): the local part only */
  if (typeof node.email === 'string' && node.email.includes('@')) {
    return node.email.slice(0, node.email.indexOf('@'))
  }
  return ''
}

const flattenNode = (value: unknown, options: PlainTextOptions): Part[] => {
  const node = asNode(value)
  if (typeof node.text === 'string') {
    return [{ text: node.text, isText: true }]
  }
  const type = typeof node.type === 'string' ? node.type : ''
  if (type === 'mention') {
    const name = mentionName(node)
    return name ? [{ text: `@${name}`, isText: true }] : []
  }
  if (type === 'chord') {
    return [{ text: `[${options.chordName(value)}]`, isText: true }]
  }
  if (type === 'entitylink' && typeof node.ref?.name === 'string') {
    return [{ text: node.ref.name, isText: true }]
  }
  if (imageTypes.has(type)) {
    return [{ text: ' [Image] ', isText: false }]
  }
  if (audioTypes.has(type)) {
    return [{ text: ' [Audio] ', isText: false }]
  }
  if (attachmentTypes.has(type)) {
    return [{ text: ' [Attachment] ', isText: false }]
  }
  const inner = childrenOf(node).flatMap(child => flattenNode(child, options))
  if (inlineTypes.has(type)) {
    return inner
  }
  return [{ text: ' ', isText: false }, ...inner, { text: ' ', isText: false }]
}

const collapse = (text: string) => text.replace(/\s+/g, ' ').trim()

const flatten = (children: unknown[], options: PlainTextOptions) =>
  children.flatMap(child => flattenNode(child, options))

/** A comment's Slate children as one line of plain text: mentions as @Name, chords as [Am7], voids named */
export const commentPlainText = (children: unknown[], options: PlainTextOptions) =>
  collapse(
    flatten(children, options)
      .map(part => part.text)
      .join('')
  )

/** Whether the comment has words, not only attachments; without them the body is N4 ("Sent an attachment") */
export const commentHasText = (children: unknown[]) =>
  flatten(children, { chordName: () => 'chord' }).some(part => part.isText && part.text.trim() !== '')

/** Cut at a word boundary so the result, ellipsis included, fits in `limit` characters */
export const truncateAtWord = (text: string, limit: number) => {
  if (text.length <= limit) {
    return text
  }
  const room = text.slice(0, limit - 1)
  /** The room already ends on a whole word when a space follows it */
  if (/\s/.test(text.charAt(limit - 1))) {
    return `${room.trimEnd()}…`
  }
  const lastSpace = room.lastIndexOf(' ')
  const cut = lastSpace > 0 ? room.slice(0, lastSpace) : room
  return `${cut.trimEnd()}…`
}
